// Distinguishes "the buyer's card was declined" from "something is broken".
//
// Every checkout failure used to be captured as a Sentry exception, so roughly 4,500 events per
// 90 days were buyers mistyping a card number or being refused by the credit company. They all
// throw from the same line, so Sentry groups them into one issue that can't be muted per-message
// — and because a changed minified frame name produces a new grouping fingerprint, "ignore" is
// undone by the next deploy. The only durable fix is not reporting them in the first place.
//
// What that noise was hiding: ~136 events of the PayMe SDK object being undefined at
// .tokenize() — a real crash at the payment step, invisible under the declines.
//
// The test is structural rather than a list of messages: PayMe's wording varies by language and
// even embeds the seller's own support address, so matching on text would rot immediately.

// The order broke the event's ticket-type selection rule (nimi-app #1556 / nimi-backend #1390).
export const TICKET_TYPE_SELECTION_ERROR = 'ticket_type_selection_out_of_range';

// Backend outcomes that are surfaced to the buyer with a userFacingMessage and are not
// engineering failures. Deliberately excludes seats_already_taken_*, which can indicate a
// double-booking race and should stay visible.
export const EXPECTED_BACKEND_ERROR_CODES = new Set([
    'error_in_create_event_order_payment', // credit-company refusal relayed by our backend
    'ticket_limit_reached',                // the ticket type sold out
    'time_slot_full',                      // the chosen slot filled up
    // A club benefit changed between check and pay, so the order was voided BEFORE any charge and the
    // buyer is told so (nimi-backend#1164 keeps this code name instead of the generic wrapper).
    'voucher_redeem_failed_order_voided',
    // The buyer's choice of ticket types broke the event's limit (a stale page, or the seller
    // changed the rule meanwhile); the BE relays a translated sentence and nothing was charged.
    TICKET_TYPE_SELECTION_ERROR,
]);

// `error_in_create_event_order_payment` is user-event's ONE generic wrapper (eventOrder.ts
// purchaseEventOrder) for every failure while charging via the payment service's generate-sale
// call — a genuine decline (payment.ts's dedicated `throw CustomError("generate_sale_failed", 400,
// status_error_details)`, relayed with statusCode 400) AND a genuine PayMe/network outage
// (payment.ts's handlePaymeError falls back to `error.httpCode || 400`, which PRESERVES payme.ts's
// own 431/432 for an actual axios/infra failure) both surface under this SAME code name — only the
// statusCode tells them apart. Unlike `ticket_limit_reached` and `time_slot_full` (each thrown from
// exactly one dedicated call site with a fixed 400), this one needs the extra check below.
const BACKEND_CODE_REQUIRING_STATUS_CHECK = 'error_in_create_event_order_payment';

const isNonEmptyString = (value) => typeof value === 'string' && value.trim() !== '';

// PayMe reports card problems either as a tokenize-error or as an ERR-* response that wraps the
// reason. Either way, the reason itself lives in payload.status_error_details.
const isPaymeCardError = (error) =>
    error?.type === 'tokenize-error' ||
    (typeof error?.code === 'string' && error.code.startsWith('ERR-'));

export const isExpectedPaymentFailure = (error) => {
    if (!error || typeof error !== 'object') return false;

    // A specific reason from the credit company means the card was declined for a known cause.
    // No reason means we genuinely don't know why it failed, so it stays reportable — that is
    // the "Unknown tokenization error" case.
    if (isPaymeCardError(error) && isNonEmptyString(error?.payload?.status_error_details)) return true;

    // The axios interceptor (utils/minimal/axios.js) rejects with a re-wrapped Error carrying the
    // response body at `details.data` — a bare `data.error` read is always undefined for our own
    // backend's errors. Accept the raw-axios and plain shapes too, as ImportEndUsersDialog does.
    const backendData = error?.details?.data ?? error?.response?.data ?? error?.data;
    const backendCode = backendData?.error;
    if (typeof backendCode !== 'string') return false;

    if (backendCode === BACKEND_CODE_REQUIRING_STATUS_CHECK) {
        // statusCode 400 only ever comes from the deliberate decline throw (see comment above) —
        // 431/432 (or anything else) means PayMe/network infra failed, which must stay reportable.
        return backendData?.statusCode === 400;
    }

    return EXPECTED_BACKEND_ERROR_CODES.has(backendCode);
}
